import { useSignIn } from '@clerk/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button } from '~/components/buttons/Button/Button';
import { FormFeedback } from '~/components/forms/FormFeedback';
import { FormLayout } from '~/components/forms/FormLayout';
import { TextInput } from '~/components/forms/TextInput/TextInput';
import { useCustomForm } from '~/hooks/useCustomForm';
import { paths } from '~/routing/routes';
import {
  getSessionTaskPath,
  readSessionTaskCompletionTarget,
  rememberSessionTaskCompletionTarget,
} from '~/routing/sessionTaskHandoff';
import { getClerkErrorMessage } from './clerkError';
import { LoginImages } from './LoginImages';
import { SatelliteAuthUnavailable } from './SatelliteAuthUnavailable';
import { useSatelliteRedirect } from './useSatelliteRedirect';

const emailSchema = z.object({ email: z.string().email('Enter a valid email') });
const resetSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
type EmailValues = z.infer<typeof emailSchema>;
type ResetValues = z.infer<typeof resetSchema>;

export const ForgotPasswordPage = () => {
  const { redirecting, blocked } = useSatelliteRedirect();
  const { signIn, fetchStatus } = useSignIn();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (readSessionTaskCompletionTarget()) return;
    rememberSessionTaskCompletionTarget(paths.landing);
  }, []);

  const emailMethods = useCustomForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });
  const resetMethods = useCustomForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { code: '', password: '' },
  });

  const onEmailSubmit = async (values: EmailValues) => {
    setServerError(null);
    setLoading(true);
    try {
      const { error: createError } = await signIn.create({ identifier: values.email });
      if (createError) {
        setServerError(getClerkErrorMessage(createError, 'Request failed.'));
        return;
      }

      const { error: sendCodeError } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendCodeError) {
        setServerError(getClerkErrorMessage(sendCodeError, 'Request failed.'));
        return;
      }

      setStep('reset');
    } catch (err: unknown) {
      setServerError(getClerkErrorMessage(err, 'Request failed.'));
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (values: ResetValues) => {
    setServerError(null);
    setLoading(true);
    try {
      // A rejected password leaves the attempt at needs_new_password. Do not
      // consume the one-time code again when the user retries with a new value.
      if (signIn.status !== 'needs_new_password') {
        const { error: verifyCodeError } = await signIn.resetPasswordEmailCode.verifyCode({
          code: values.code,
        });
        if (verifyCodeError) {
          setServerError(getClerkErrorMessage(verifyCodeError, 'Reset failed.'));
          return;
        }
      }

      const { error: submitPasswordError } = await signIn.resetPasswordEmailCode.submitPassword({
        password: values.password,
      });
      if (submitPasswordError) {
        setServerError(getClerkErrorMessage(submitPasswordError, 'Reset failed.'));
        return;
      }

      if (signIn.status === 'complete') {
        const { error: finalizeError } = await signIn.finalize({
          navigate: ({ session, decorateUrl }) => {
            if (session?.currentTask) {
              // ClerkProvider taskUrls normally takes over. Keep a local fallback
              // for adapters that still invoke this callback with a pending task.
              const taskPath = getSessionTaskPath(session.currentTask.key);
              if (taskPath) navigate(taskPath, { replace: true });
              return;
            }

            const destination = decorateUrl(paths.landing);
            if (destination.startsWith('http')) {
              window.location.assign(destination);
              return;
            }
            navigate(destination, { replace: true });
          },
        });
        if (finalizeError) {
          setServerError(getClerkErrorMessage(finalizeError, 'Reset failed.'));
        }
      } else if (
        signIn.status === 'needs_second_factor' ||
        signIn.status === 'needs_client_trust' ||
        signIn.status === 'needs_protect_check'
      ) {
        navigate(paths.publicAuth.signIn, { replace: true });
      } else {
        setServerError('Password reset could not be completed. Please try again.');
      }
    } catch (err: unknown) {
      setServerError(getClerkErrorMessage(err, 'Reset failed.'));
    } finally {
      setLoading(false);
    }
  };

  if (blocked) {
    return <SatelliteAuthUnavailable />;
  }

  if (redirecting) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-primary-t-950">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col items-center justify-center gap-6 max-w-sm w-full">
          {step === 'email' && (
            <>
              <div className="self-start">
                <h1 className="heading-large text-grey-100">Reset password</h1>
                <p className="text-sm text-grey-400 mt-1">
                  Enter your email and we'll send a reset code.
                </p>
              </div>
              <FormProvider {...emailMethods}>
                {/* The <form> keeps `w-full`: its parent is `items-center`, so
                    without it the form would shrink to its content width and
                    FormLayout's own w-full would have nothing to fill. */}
                <form
                  onSubmit={emailMethods.handleSubmit(onEmailSubmit)}
                  className="w-full"
                  noValidate
                >
                  <FormLayout>
                    <TextInput
                      name="email"
                      label="Email"
                      type="email"
                      autoComplete="email"
                      required
                    />
                    <FormFeedback
                      error={serverError ?? undefined}
                      errorId="forgot-password-error"
                    />
                    <Button
                      label={loading ? 'Sending…' : 'Send reset code'}
                      type="submit"
                      disabled={loading || fetchStatus === 'fetching'}
                      variant="bar"
                    />
                  </FormLayout>
                </form>
              </FormProvider>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="self-start">
                <h1 className="heading-large text-grey-100">Check your email</h1>
                <p className="text-sm text-grey-400 mt-1">
                  Enter the code we sent, then choose a new password.
                </p>
              </div>
              <FormProvider {...resetMethods}>
                <form
                  onSubmit={resetMethods.handleSubmit(onResetSubmit)}
                  className="w-full"
                  noValidate
                >
                  <FormLayout>
                    <TextInput
                      name="code"
                      label="Reset code"
                      autoComplete="one-time-code"
                      required
                    />
                    <TextInput
                      name="password"
                      label="New password"
                      type="password"
                      autoComplete="new-password"
                      required
                    />
                    <FormFeedback
                      error={serverError ?? undefined}
                      errorId="forgot-password-error"
                    />
                    <Button
                      label={loading ? 'Resetting…' : 'Reset password'}
                      type="submit"
                      disabled={loading || fetchStatus === 'fetching'}
                      variant="bar"
                    />
                  </FormLayout>
                </form>
              </FormProvider>
            </>
          )}

          <Link to={paths.publicAuth.signIn} className="text-sm text-primary-50 hover:underline">
            Back to sign in
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center">
          <LoginImages />
        </div>
      </div>
    </div>
  );
};
